// i18n
import { useTrans } from "@/i18n"

// Icons
import { FiAlertTriangle, FiCheckCircle, FiClock, FiInfo } from "@/lib/icons"

// Queries
import { UsersUserMangopayRecipientStatusChoices } from "@/api/graphql"

// Utils
import { cn } from "@/lib/utils"

type RecipientStatusConfig = {
	icon: React.ReactNode
	container: string
	titleClass: string
	bodyClass: string
	titleKey: string
	bodyKey: string
}

function getRecipientStatusConfig(
	status: UsersUserMangopayRecipientStatusChoices,
): RecipientStatusConfig | null {
	switch (status) {
		case UsersUserMangopayRecipientStatusChoices.Active:
			return {
				icon: <FiCheckCircle className="size-5 text-green-500" />,
				container: "bg-green-50",
				titleClass: "text-green-800",
				bodyClass: "text-green-700",
				titleKey: "profile:bank_details.status.active.title",
				bodyKey: "profile:bank_details.status.active.body",
			}
		case UsersUserMangopayRecipientStatusChoices.Pending:
			return {
				icon: <FiClock className="size-5 text-yellow-500" />,
				container: "bg-yellow-50",
				titleClass: "text-yellow-800",
				bodyClass: "text-yellow-700",
				titleKey: "profile:bank_details.status.pending.title",
				bodyKey: "profile:bank_details.status.pending.body",
			}
		case UsersUserMangopayRecipientStatusChoices.Canceled:
			return {
				icon: <FiAlertTriangle className="size-5 text-red-500" />,
				container: "bg-red-50",
				titleClass: "text-red-800",
				bodyClass: "text-red-700",
				titleKey: "profile:bank_details.status.canceled.title",
				bodyKey: "profile:bank_details.status.canceled.body",
			}
		case UsersUserMangopayRecipientStatusChoices.Deactivated:
			return {
				icon: <FiInfo className="size-5 text-gray-500" />,
				container: "bg-gray-50",
				titleClass: "text-gray-800",
				bodyClass: "text-gray-700",
				titleKey: "profile:bank_details.status.deactivated.title",
				bodyKey: "profile:bank_details.status.deactivated.body",
			}
		default:
			return null
	}
}

/**
 * Render a small banner that reflects the Mangopay Recipient lifecycle status
 * so the user understands why their IBAN may not yet be usable for payouts.
 *
 *  - PENDING: SCA challenge started, waiting for the user to complete it.
 *  - ACTIVE:  SCA succeeded, payouts permitted.
 *  - CANCELED: SCA abandoned or rejected — user must resubmit IBAN.
 *  - DEACTIVATED: terminal manual deactivation.
 */
export function RecipientStatusBanner({
	status,
	maskedIban,
}: {
	status: UsersUserMangopayRecipientStatusChoices
	maskedIban: string | null
}) {
	const t = useTrans("profile")

	const config = getRecipientStatusConfig(status)

	if (!config) {
		return null
	}

	return (
		<div
			className={cn("mb-4 rounded-md p-4", config.container)}
			data-testid="bank-details-status-banner"
			data-status={status}
		>
			<div className="flex items-start">
				<div className="shrink-0">{config.icon}</div>
				<div className="ml-3">
					<h3
						className={cn("text-sm font-medium", config.titleClass)}
					>
						{t(config.titleKey)}
					</h3>
					<p className={cn("mt-1 text-sm", config.bodyClass)}>
						{t(config.bodyKey, {
							iban: maskedIban ?? "",
						})}
					</p>
				</div>
			</div>
		</div>
	)
}
